import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

// Segments in the order they matter for sending. `key` matches the status
// returned by getAccountSegments; `all` and `unverified` are cross-cuts rather
// than statuses, so they filter differently.
const SEGMENTS = [
  { id: 'all', label: 'All accounts', hint: 'Everyone with an account' },
  { id: 'paying', label: 'Paying', hint: 'Active Stripe subscription' },
  { id: 'cancelledInSwitch', label: 'Cancelled in switch', hint: 'Subscription cancelled when search went free' },
  { id: 'former', label: 'Former subscribers', hint: 'Cancelled or lapsed checkout' },
  { id: 'free', label: 'Free', hint: 'Never had premium' },
  { id: 'comp', label: 'Comp', hint: 'Access code or hand-set' },
  { id: 'unverified', label: 'Unverified', hint: 'Never confirmed their email' },
];

const filterFor = (id) => (a) => {
  if (id === 'all') return true;
  if (id === 'unverified') return !a.emailVerified;
  if (id === 'cancelledInSwitch') return a.cancelledInSwitch;
  return a.status === id;
};

const shortDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '—');

function Stat({ label, value, sub, warn }) {
  return (
    <div>
      <p className="tiu-muted text-xs uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-semibold tabular-nums ${warn ? 'text-red-700' : ''}`}>{value}</p>
      {sub && <p className="tiu-muted text-xs">{sub}</p>}
    </div>
  );
}

function AdminPortal() {
  const { currentUser } = useAuth();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [active, setActive] = useState('all');
  const [copied, setCopied] = useState(false);

  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setState({ status: 'loading', data: null, error: null });
    try {
      const call = httpsCallable(functions, 'getAccountSegments');
      const { data } = await call();
      setState({ status: 'ready', data, error: null });
      // Stats are secondary - a failure here must not hide the account lists.
      httpsCallable(functions, 'getSystemStats')()
        .then(({ data: s }) => setStats(s))
        .catch(() => setStats(null));
    } catch (err) {
      setState({
        status: 'error',
        data: null,
        error:
          err.code === 'functions/permission-denied'
            ? 'This account is not an admin.'
            : err.message || 'Could not load accounts.',
      });
    }
  }, []);

  useEffect(() => {
    if (currentUser) load();
  }, [currentUser, load]);

  // Memoised so the reference is stable - otherwise every render produces a new
  // array and the filters below recompute on each one.
  const accounts = useMemo(() => (state.data ? state.data.accounts : []), [state.data]);
  const rows = useMemo(() => accounts.filter(filterFor(active)), [accounts, active]);
  const emails = useMemo(
    () => rows.map((r) => r.email).filter(Boolean),
    [rows]
  );

  const copyEmails = async () => {
    await navigator.clipboard.writeText(emails.join(', '));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCsv = () => {
    const header = ['email', 'status', 'displayName', 'provider', 'emailVerified', 'created', 'lastSignIn'];
    const cell = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      header.join(','),
      ...rows.map((r) => header.map((h) => cell(r[h])).join(',')),
    ].join('\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `teeitup-${active}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!currentUser) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="tiu-h1 mb-3">Admin</h1>
        <p className="tiu-muted mb-6">Sign in to continue.</p>
        <Link to="/login" className="tiu-btn tiu-btn-primary">Sign in</Link>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="tiu-h1 mb-3">Admin</h1>
        <div className="tiu-alert-error mb-6">{state.error}</div>
        <Link to="/" className="tiu-btn tiu-btn-secondary">Back to search</Link>
      </div>
    );
  }

  const totals = state.data ? state.data.totals : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="tiu-h1">Accounts</h1>
          <p className="tiu-muted">
            {state.status === 'loading'
              ? 'Loading…'
              : `${totals.all} accounts · updated ${new Date(state.data.generatedAt).toLocaleTimeString()}`}
          </p>
        </div>
        <button onClick={load} className="tiu-btn tiu-btn-secondary" disabled={state.status === 'loading'}>
          Refresh
        </button>
      </div>

      {state.status === 'loading' && <div className="tiu-card p-8 text-center tiu-muted">Loading accounts…</div>}

      {stats && (
        <div className="tiu-card p-4 mb-5">
          <h2 className="tiu-h2 mb-3">System</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
            <Stat
              label="Emails today"
              value={`${stats.email.today} / ${stats.email.dailyLimit}`}
              warn={stats.email.today >= stats.email.dailyLimit * 0.8}
            />
            <Stat
              label="Emails this month"
              value={`${stats.email.month} / ${stats.email.monthlyLimit}`}
              warn={stats.email.month >= stats.email.monthlyLimit * 0.8}
            />
            <Stat label="Active alerts" value={stats.alerts.activeWatches}
              sub={`${stats.alerts.uniqueWatchers} people`} />
            <Stat label="Poll targets" value={stats.alerts.activePollTargets}
              sub="course/date pairs" />
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm tiu-muted">
            <span>
              Poller:{' '}
              {stats.poller.lastRunAt
                ? `ran ${stats.poller.staleSeconds}s ago (${stats.poller.lastRunMs}ms, ${stats.poller.targetsClaimed} targets)`
                : 'has not run yet'}
            </span>
            <span>SMS: {stats.sms.configured ? `${stats.sms.month} this month` : 'not configured'}</span>
            {stats.email.failedMonth > 0 && (
              <span className="text-red-700">{stats.email.failedMonth} delivery failures this month</span>
            )}
          </div>

          {Object.keys(stats.poller.vendors).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {Object.entries(stats.poller.vendors).map(([vendor, v]) => (
                <span key={vendor}
                  className={`tiu-badge ${v.failed > 0 ? 'bg-red-50 text-red-700' : ''}`}>
                  {vendor} {v.ok}✓{v.failed ? ` ${v.failed}✗` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {state.status === 'ready' && (
        <>
          {/* Segment picker doubles as the summary - counts live on the tabs
              rather than in a separate stat row nobody would read twice. */}
          <div className="flex flex-wrap gap-2 mb-5">
            {SEGMENTS.map((s) => {
              const count = accounts.filter(filterFor(s.id)).length;
              const on = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  title={s.hint}
                  className={`tiu-chip ${on ? 'is-selected' : ''}`}
                  aria-pressed={on}
                >
                  {s.label}
                  <span className="ml-2 font-semibold tabular-nums">{count}</span>
                </button>
              );
            })}
          </div>

          {totals.orphanedBillingRecords > 0 && (
            <div className="tiu-alert-error mb-5">
              {totals.orphanedBillingRecords} billing record(s) have no Firebase account — a user deleted
              their account while a subscription record remained.
            </div>
          )}

          <div className="tiu-card p-4 mb-5">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button onClick={copyEmails} className="tiu-btn tiu-btn-primary" disabled={!emails.length}>
                {copied ? 'Copied' : `Copy ${emails.length} addresses`}
              </button>
              <button onClick={downloadCsv} className="tiu-btn tiu-btn-secondary" disabled={!rows.length}>
                Download CSV
              </button>
              <span className="tiu-muted">Paste into the BCC field.</span>
            </div>
            <textarea
              readOnly
              value={emails.join(', ')}
              rows={4}
              className="tiu-input font-mono text-xs"
              aria-label="Email addresses in this segment"
              onFocus={(e) => e.target.select()}
            />
          </div>

          <div className="tiu-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left tiu-muted border-b border-slate-200">
                    <th className="p-3 font-medium">Email</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Joined</th>
                    <th className="p-3 font-medium">Last seen</th>
                    <th className="p-3 font-medium">Sign-in</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.uid} className="border-b border-slate-100 last:border-0">
                      <td className="p-3">
                        {a.email || <span className="tiu-muted">no email</span>}
                        {!a.emailVerified && <span className="tiu-badge ml-2">unverified</span>}
                      </td>
                      <td className="p-3">{a.status}</td>
                      <td className="p-3 tabular-nums">{shortDate(a.created)}</td>
                      <td className="p-3 tabular-nums">{shortDate(a.lastSignIn)}</td>
                      <td className="p-3 tiu-muted">{a.provider.replace('.com', '')}</td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan={5} className="p-6 text-center tiu-muted">No accounts in this segment.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminPortal;
